import { useEffect, useRef, useCallback, useState } from "react";

/**
 * useWebSocket — Reusable WebSocket hook with auto-reconnect
 * @param {string} path - WS path (e.g., "/api/ws/monitor")
 * @param {function} onMessage - Callback for incoming messages
 * @param {object} options - { enabled, reconnectInterval, maxRetries }
 */
export function useWebSocket(path, onMessage, options = {}) {
  const { enabled = true, reconnectInterval = 3000, maxRetries = 10 } = options;
  const wsRef = useRef(null);
  const retriesRef = useRef(0);
  const reconnectTimerRef = useRef(null);
  const onMessageRef = useRef(onMessage);
  const [connected, setConnected] = useState(false);

  // Keep callback ref fresh without re-triggering effect
  useEffect(() => {
    onMessageRef.current = onMessage;
  }, [onMessage]);

  const connect = useCallback(() => {
    if (!enabled || !path) return;

    // Build wss:// URL from the current page origin
    const baseUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
    const wsUrl = baseUrl.replace(/^http/, "ws") + path;

    try {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        setConnected(true);
        retriesRef.current = 0;
        // Start keepalive ping
        ws._pingInterval = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send("ping");
          }
        }, 25000);
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === "pong") return; // ignore keepalive
          onMessageRef.current(data);
        } catch {
          // ignore non-JSON messages
        }
      };

      ws.onclose = () => {
        setConnected(false);
        clearInterval(ws._pingInterval);
        // Auto-reconnect
        if (retriesRef.current < maxRetries && enabled) {
          retriesRef.current += 1;
          reconnectTimerRef.current = setTimeout(connect, reconnectInterval);
        }
      };

      ws.onerror = () => {
        // onclose will fire after this
      };
    } catch {
      // Reconnect on failure
      if (retriesRef.current < maxRetries && enabled) {
        retriesRef.current += 1;
        reconnectTimerRef.current = setTimeout(connect, reconnectInterval);
      }
    }
  }, [path, enabled, reconnectInterval, maxRetries]);

  useEffect(() => {
    connect();
    return () => {
      clearTimeout(reconnectTimerRef.current);
      if (wsRef.current) {
        clearInterval(wsRef.current._pingInterval);
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [connect]);

  return { connected, ws: wsRef };
}
